/**
 * The one place this app talks to `backoffice-api`.
 *
 * Lives outside `src/api/` deliberately: Orval regenerates that directory with `clean: true`, so
 * anything hand-written there is erased on the next `bun run gen`.
 */

/** Every response is wrapped, success or failure. */
interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: {
    category: string;
    code: string;
    message: string;
    retriable?: boolean;
    details?: unknown;
  };
}

/**
 * A refusal, carrying the code rather than the status.
 *
 * **Branch on `code`, never on `status`.** Several codes share `409` and several share `400`; the
 * status alone collapses distinct outcomes into one handler. This is the same rule the kiosk
 * contract makes normative for the device side, and it exists because a `409` on this API can mean
 * six different things.
 */
export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
    public readonly category: string,
    public readonly details?: unknown,
    /** Present on 429. Seconds until the lock lifts. */
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = "ApiError";
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * The one refusal that is not about the request. A shared source IP — which a misconfigured
   * reverse proxy produces even on personal machines — means one person's mistyped password locks
   * the counter for everyone, and successful logins never clear the IP key. So this needs a UI
   * state saying "wait, it is not your password", never a generic error toast.
   */
  get isRateLimited(): boolean {
    return this.status === 429;
  }

  /** A dependency did not answer. The only category worth an automatic retry. */
  get isUpstream(): boolean {
    return this.code === "UPSTREAM_UNAVAILABLE";
  }
}

/**
 * What a rejected composable actually carries — read by Orval, not by this file.
 *
 * Orval looks for the literal string `export type ErrorType` in a mutator's source and, finding it,
 * wraps every generated `TError` in it: `ErrorType<BackofficeLogin400 | BackofficeLogin401 | …>`.
 * Without it those per-status response models ARE the error type, and they are never what surfaces:
 * `apiFetch` reads the envelope's `error` object and throws `ApiError`, so a caller reaching for
 * `.code` — the thing `CLAUDE.md` makes normative to branch on — got a compile error instead, and
 * the union it was offered describes a wire body no code in this app ever holds.
 *
 * `E` is deliberately discarded. It is the spec's list of error envelopes for the route, and every
 * one of them has already been consumed by the time a caller sees anything: category, code, message
 * and details are read off it into `ApiError`, which additionally carries the HTTP status the body
 * cannot. Narrowing by status is what the union invites and what the codes forbid — six different
 * outcomes share `409`.
 *
 * This is the same defect as the response envelope, on the rejection path, and it has the same fix
 * shape: say the true thing once, at the generator's seam, rather than casting at every call site.
 */
export type ErrorType<E> = ApiError;

/**
 * `localStorage`, not `sessionStorage` — chosen knowingly, not by default. It survives both a
 * reload AND a new tab (`sessionStorage` survives neither a closed tab nor, on most browsers, a
 * duplicated one), which is the whole point: a manager mid-reconciliation who hits F5 must not be
 * bounced to the most rate-limited route in the API. The cost is a wider XSS blast radius — any
 * script that runs in this origin can read this key, where `sessionStorage` would at least confine
 * it to the tab that was compromised. Accepted deliberately: this app has no user-generated content
 * surface to inject through, and the alternative (forcing re-login on every reload) is the actual
 * usability defect this exists to close. Re-litigating this trade means reopening the ADR/design
 * conversation that made it, not editing this comment.
 *
 * Exported so tests can seed or inspect the key directly rather than risk drifting from what
 * `setToken` actually writes.
 */
export const TOKEN_STORAGE_KEY = "backoffice.token";

let token: string | null = null;

/** Set at login, cleared at logout and on 401. The token carries `{staffId}` and nothing else —
 *  never decode it for authorization decisions; `GET /v1/me` is the authority.
 *
 *  Writes through to `localStorage` (see `TOKEN_STORAGE_KEY` for why that store and not
 *  `sessionStorage`) so a reload has something to rehydrate from — see
 *  `features/auth/rehydrate.ts`. The write is wrapped in `try/catch`: Safari private-mode and a
 *  user with storage disabled both throw on `setItem`/`removeItem` rather than no-op, and a token
 *  that still works for THIS tab's in-memory requests is strictly better than one that crashes
 *  login because disk failed. */
export function setToken(next: string | null): void {
  token = next;
  try {
    if (next) localStorage.setItem(TOKEN_STORAGE_KEY, next);
    else localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    // Degrade to in-memory-only for this tab rather than throwing through a login or logout.
  }
}

/** The in-memory token, as every request actually sees it right now. */
export function getToken(): string | null {
  return token;
}

/**
 * A pure disk read, independent of the in-memory token. Rehydration's "is there anything to do"
 * check must not depend on the module variable it is about to populate — at page load `token` is
 * always `null` regardless of what is stored, so that check has to go straight to `localStorage`.
 */
export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Mirrors the in-memory token to a value ALREADY on disk, without writing it again.
 *
 * The one caller is the cross-tab `storage` listener (`app/storage-sync.ts`): by the time that
 * event fires, the tab that caused it has already written `localStorage`, so writing the same key
 * again from here would race whatever that tab — or a third one — writes next. `setToken` is for a
 * value THIS tab is originating (login, logout, a rehydration verdict); this is for a value
 * another tab already settled.
 */
export function adoptStoredToken(next: string | null): void {
  token = next;
}

/**
 * `crypto.randomUUID` is secure-context only (HTTPS or `localhost`). A casino backoffice served
 * over plain `http://` on an internal hostname or IP is a plausible LAN deployment, and on that
 * origin `crypto.randomUUID` is `undefined` — calling it unconditionally threw a `TypeError` before
 * `fetch` was ever reached, so not one request succeeded (`docs/UNOBSERVED.md` §1.7). Logged once,
 * not per request: the operator does not need to know on every click, IT needs to know once that
 * this deployment is unencrypted.
 */
let warnedInsecureContext = false;

/** Monotonic within this page load, so two ids can never collide even if `Date.now()` and
 *  `Math.random()` both do. Reset only by a reload, which is exactly when a fresh trace starts. */
let fallbackRequestCounter = 0;

/**
 * Used only when `crypto.randomUUID` is unavailable. Not cryptographically random — `x-request-id`
 * is a trace correlation key, not a security token — but still practically unique: a per-load
 * counter that never repeats, salted with time and randomness so ids from different tabs or page
 * loads don't collide either.
 */
function fallbackRequestId(): string {
  fallbackRequestCounter += 1;
  const random = Math.random().toString(36).slice(2, 10);
  return `req-${Date.now().toString(36)}-${fallbackRequestCounter}-${random}`;
}

/** A per-request id, forwarded so a trace survives across all four services. */
function requestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (!warnedInsecureContext) {
    warnedInsecureContext = true;
    console.warn(
      "crypto.randomUUID() is unavailable — this page is likely served over an insecure " +
        "(non-HTTPS) origin. Falling back to a non-cryptographic request id; every request will " +
        "still get a unique x-request-id, but ask IT to serve this app over HTTPS.",
    );
  }
  return fallbackRequestId();
}

type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * The seam that lets `apiFetch` announce "a request just proved the session is over" without this
 * file knowing anything about Pinia or the router — this is "the one place this app talks to
 * `backoffice-api`" (this file's own top comment), not the one place that decides what a dead
 * session means for navigation. `app/session-guard.ts` is the one caller, registered once at
 * startup, mirroring how `app/storage-sync.ts` wires `watchTokenAcrossTabs()`.
 *
 * Fired for EVERY request that answers `STAFF_TOKEN_INVALID`, not only the one-shot
 * `features/auth/rehydrate.ts` runs at page load — see that module's own comment for why a
 * one-shot cannot see a token that dies mid-shift (`docs/UNOBSERVED.md` §1.5).
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

type IdentityStaleHandler = (code: string) => void;
let identityStaleHandler: IdentityStaleHandler | null = null;

/**
 * Codes that mean "who you are has changed under you" rather than "this request was wrong": a role
 * changed mid-session (`ROLE_FORBIDDEN` — CLAUDE.md: re-fetch `/v1/me` after any surprising 403),
 * or the account was deactivated (`STAFF_INACTIVE`). Announced through the same kind of seam as
 * `setUnauthorizedHandler`, so no screen needs its own re-fetch branch; `app/session-guard.ts`
 * registers the one handler, which re-resolves identity from `/v1/me`.
 */
const IDENTITY_STALE_CODES = new Set(["ROLE_FORBIDDEN", "STAFF_INACTIVE"]);

export function setIdentityStaleHandler(handler: IdentityStaleHandler | null): void {
  identityStaleHandler = handler;
}

/**
 * How long any one request may take before this app gives up on it. A hung request otherwise
 * leaves a button pending forever. Every money write here is idempotent by its own key (a
 * withdrawal's `eventId`, a sign-off's date), so a timed-out write is safe to retry: the server
 * either finished it or did not, and the retry finds out which.
 */
export const REQUEST_TIMEOUT_MS = 30_000;

/**
 * The 401 `backoffice-api` answers on sign-off and break-glass when the token's proof of password
 * is too old (~5 minutes). Not a dead session: the caller re-runs its step-up. Deliberately NOT
 * routed to the unauthorized handler, which ends the session.
 */
export const STEP_UP_REQUIRED = "STEP_UP_REQUIRED";

/** The code a timed-out request throws with — never retried by `query-client.ts` (not upstream). */
export const REQUEST_TIMEOUT_CODE = "REQUEST_TIMEOUT";

/**
 * Prefers a standard `Retry-After` response header; falls back to `error.details.retryAfterSeconds`
 * — which is what `backoffice-api` actually sends on a 429 today. `CoreError.toResponse` (this
 * platform's shared error-to-HTTP mapping) only ever calls `c.json(...)`, on every route, so no
 * response anywhere in this API sets the header at all. Without this fallback `ApiError.retryAfter`
 * was `undefined` on every real 429, silently and permanently: `login-error.ts`'s `classifyLoginError`
 * reads `err.retryAfter ?? 60`, so a caller staring at a fifteen-minute lock (S7's cap) was always
 * told "wait 1 minute" instead. No mocked-fetch test can catch this — a test mock chooses its own
 * header/body pairing and never encounters a real service that answers the value one way but not
 * the other; only a live round trip (`src/e2e/live.test.ts`) does.
 */
function retryAfterSeconds(res: Response, details: unknown): number | undefined {
  const header = res.headers.get("retry-after");
  if (header !== null) {
    const fromHeader = Number(header);
    if (!Number.isNaN(fromHeader)) return fromHeader;
  }
  if (details !== null && typeof details === "object" && "retryAfterSeconds" in details) {
    const fromDetails = (details as { retryAfterSeconds: unknown }).retryAfterSeconds;
    if (typeof fromDetails === "number") return fromDetails;
  }
  return undefined;
}

/**
 * The optional fields carry an explicit `| undefined` rather than relying on `?` alone.
 *
 * `exactOptionalPropertyTypes` is on, which makes an absent key and an explicitly-undefined key
 * different types — and Orval's generated call sites always pass every field, undefined included.
 * Without the union the generated tree does not typecheck, which is a real signal: this interface
 * is a contract with a code generator, not with a hand-written caller.
 */
export interface FetchArgs {
  url: string;
  method: string;
  params?: Record<string, unknown> | undefined;
  data?: unknown;
  headers?: Record<string, string> | undefined;
  signal?: AbortSignal | undefined;
}

/**
 * Where the API lives, when it is not this page's own origin.
 *
 * Empty by default, which keeps every existing caller and the Vite dev proxy working exactly as
 * before — a relative `/v1/...` resolves against the page. Set `VITE_API_BASE_URL` at build time
 * when the API is on another hostname, which is the Cloudflare Pages deployment: Pages has no
 * server side, so nothing there can proxy `/v1` to the API.
 *
 * No trailing slash: the generated paths all begin with `/`.
 */
export function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");
}

/**
 * Orval's mutator. Unwraps the envelope so generated composables return `data` directly, and turns
 * a refusal into an `ApiError` carrying the code.
 *
 * `backoffice-api` ships no CORS middleware, so a cross-origin call fails preflight and never
 * reaches the server unless the server opts in. Locally, the dev proxy makes every request
 * same-origin so no CORS header is ever needed. In production `apiBase()` points `/v1/...` at
 * `backoffice-api` on its own hostname — Cloudflare Pages has no server side to proxy through —
 * and it is CORS configured on `backoffice-api` itself, not same-origin-ness, that permits the
 * call.
 */
export async function apiFetch<T>({
  url,
  method,
  params,
  data,
  headers,
  signal,
}: FetchArgs): Promise<T> {
  const qs = params
    ? `?${new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .map(([k, v]) => [k, String(v)]),
      )}`
    : "";

  // Built via `Headers.set`, not an object spread, and that is load-bearing: every generated
  // mutation composable ALSO passes its own `Content-Type` (capitalized) on top of the default
  // `content-type` below, and a plain `{...defaults, ...headers}` spread keeps both — they are
  // different JS object keys even though HTTP treats them as the same header. `fetch` then folds
  // same-name headers together with `, `, so the wire value becomes
  // `application/json, application/json`, and `backoffice-api`'s request validation does not
  // recognize that as JSON — every field in the body comes back `undefined`, answering `400
  // VALIDATION_ERROR` no matter what was sent. `.set` is a case-insensitive REPLACE, so the
  // caller's own header simply wins, which is what "the caller can override a default" was always
  // supposed to mean. This was invisible to every other test in this repo: all of them stub
  // `fetch` itself, so nothing here ever reaches the real `Headers` folding behavior or a real
  // server's content-type check — only a live round trip exercises it, which is what
  // `src/e2e/live.test.ts` is for.
  const finalHeaders = new Headers();
  finalHeaders.set("content-type", "application/json");
  finalHeaders.set("x-request-id", requestId());
  if (token) finalHeaders.set("authorization", `Bearer ${token}`);
  if (headers) {
    for (const [key, value] of Object.entries(headers)) finalHeaders.set(key, value);
  }

  // One controller carrying both the caller's cancellation (Vue Query's) and our own timeout.
  // Built by hand rather than with `AbortSignal.any`/`.timeout`, which older kiosk-floor browsers
  // and the jsdom test realm do not all have.
  const controller = new AbortController();
  let timedOut = false;
  const timeoutError = () =>
    new ApiError(
      REQUEST_TIMEOUT_CODE,
      0,
      `No answer within ${REQUEST_TIMEOUT_MS / 1000}s`,
      "timeout",
    );
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  const forwardAbort = () => controller.abort(signal?.reason);
  if (signal) {
    if (signal.aborted) forwardAbort();
    else signal.addEventListener("abort", forwardAbort, { once: true });
  }

  let res: Response;
  let body: Envelope<T> | null;
  try {
    res = await fetch(`${apiBase()}${url}${qs}`, {
      method: method.toUpperCase(),
      signal: controller.signal,
      headers: finalHeaders,
      ...(data === undefined ? {} : { body: JSON.stringify(data) }),
    });
    body = (await res.json().catch(() => null)) as Envelope<T> | null;
  } catch (err) {
    if (timedOut) throw timeoutError();
    throw err;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", forwardAbort);
  }
  // The headers arrived but the body did not: `res.json()`'s own catch turned the abort into null.
  if (timedOut && body === null) throw timeoutError();

  if (!res.ok || !body?.success) {
    const e = body?.error;
    // Announced BEFORE throwing, not in a `.catch()` at some call site — every one of them would
    // otherwise need its own STAFF_TOKEN_INVALID branch, which is exactly the per-screen guessing
    // `docs/UNOBSERVED.md` §1.5 describes. `unauthorizedHandler` itself decides whether there is a
    // session to end (see `session-guard.ts`) — this file only reports the code.
    if (e?.code === "STAFF_TOKEN_INVALID") unauthorizedHandler?.();
    if (e?.code && IDENTITY_STALE_CODES.has(e.code)) identityStaleHandler?.(e.code);
    throw new ApiError(
      e?.code ?? "UNKNOWN",
      res.status,
      e?.message ?? `Request failed with ${res.status}`,
      e?.category ?? "unknown",
      e?.details,
      retryAfterSeconds(res, e?.details),
    );
  }

  return body.data as T;
}
